import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowRight, AlertCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import SectionHeading from "../components/SectionHeading";
import { api } from "../lib/api";

const initial = {
  athlete_first_name: "",
  athlete_last_name: "",
  athlete_age: "",
  athlete_dob: "",
  parent_name: "",
  parent_phone: "",
  parent_email: "",
  address: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
  medical_notes: "",
  shirt_size: "",
  agreement: false,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CheerRegister() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [emailSent, setEmailSent] = useState(true);

  const update = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((s) => ({ ...s, [k]: v }));
  };

  const validate = () => {
    if (!form.athlete_first_name.trim()) return "Cheerleader first name is required.";
    if (!form.athlete_last_name.trim()) return "Cheerleader last name is required.";
    const ageNum = parseInt(form.athlete_age, 10);
    if (!ageNum || ageNum < 6 || ageNum > 13) return "Please enter a valid age between 6 and 13.";
    if (!form.parent_name.trim()) return "Parent / Guardian name is required.";
    if (!form.parent_phone.trim()) return "Parent phone number is required.";
    if (!EMAIL_RE.test(form.parent_email.trim())) return "Please enter a valid parent email address.";
    if (!form.agreement) return "Please accept the agreement to register.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/cheer-registrations", {
        ...form,
        athlete_age: parseInt(form.athlete_age, 10),
      });
      const sent = !!res?.data?.email_sent;
      setEmailSent(sent);
      setSuccess(true);
      setForm(initial);
      if (sent) {
        toast.success("Cheer registration received successfully.");
      } else {
        toast.success("Your submission was received. Our team will review it soon.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      console.error("[cheer-registration] submit failed:", err);
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="cheer-register-success" className="min-h-[70vh] flex items-center justify-center px-6 py-24 bg-[#F5EFE0]">
        <div className="max-w-xl text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#2D5F3F] mb-8 shadow-ink-offset">
            <CheckCircle2 className="w-10 h-10 text-[#FDFBF7]" strokeWidth={2.5} />
          </div>
          <h1 className="font-display text-5xl sm:text-6xl font-black uppercase text-[#0A0A0A] leading-none">
            {emailSent ? "Cheer squad, welcome!" : "Submission saved"}
          </h1>
          <p className="mt-6 text-lg text-black/75">
            {emailSent
              ? <>Registration received successfully. A coach will reach out with next steps, uniform fitting, and practice details. Remember to bring your <span className="text-[#2D5F3F] font-bold">Sports Physical, Medical Insurance, and Birth Certificate</span> to first practice.</>
              : <>Your submission was received. Our team will review it soon. We&apos;ll be in touch with next steps and remind you to bring your <span className="text-[#2D5F3F] font-bold">Sports Physical, Medical Insurance, and Birth Certificate</span> to first practice.</>
            }
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setSuccess(false)}
              data-testid="cheer-register-another"
              className="border-2 border-[#2D5F3F] text-[#2D5F3F] hover:bg-[#2D5F3F] hover:text-[#FDFBF7] font-display uppercase tracking-wider font-bold px-6 py-3 transition-colors"
            >
              Register Another
            </button>
            <Link
              to="/"
              data-testid="cheer-back-home"
              className="inline-flex items-center gap-2 bg-[#2D5F3F] hover:bg-[#1B4332] text-[#FDFBF7] font-display uppercase tracking-wider font-extrabold px-6 py-3 transition-colors"
            >
              Back Home <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cheer-register-page" className="bg-[#F5EFE0] py-20 md:py-28">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <SectionHeading
          eyebrow="2026 Cheer Season · Mount Baldy Conference"
          title="Join The Cheer Squad"
          description="Titans Cheer for ages 6–13. $225 covers uniform, bow &amp; practice wear. Practice begins July 20, 2026. Contact Autumn Andrews at (951) 590-6349."
        />

        {/* PRINT-REQUIRED NOTICE */}
        <div
          data-testid="cheer-register-print-notice"
          className="mb-10 flex flex-col md:flex-row items-start gap-5 bg-[#2D5F3F] text-[#FDFBF7] p-6 md:p-7 border-l-8 border-[#FDFBF7]"
        >
          <div className="shrink-0 w-12 h-12 bg-[#FDFBF7] text-[#2D5F3F] flex items-center justify-center">
            <AlertCircle className="w-6 h-6" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <div className="font-display text-xs uppercase tracking-[0.3em] font-bold opacity-85">Don&apos;t Forget</div>
            <h3 className="mt-1 font-display text-xl md:text-2xl font-black uppercase leading-tight">
              The Parental Consent Form &amp; Physical Form Must Be Printed and Filled Out
            </h3>
            <p className="mt-2 text-[#FDFBF7]/90 text-sm md:text-base">
              Download, print, complete, and bring both to first practice. Digital copies are not accepted.
            </p>
          </div>
          <Link
            to="/forms"
            data-testid="cheer-register-forms-link"
            className="shrink-0 inline-flex items-center gap-2 bg-[#FDFBF7] hover:bg-[#0A0A0A] text-[#2D5F3F] hover:text-[#FDFBF7] font-display uppercase tracking-wider font-extrabold px-5 py-3 text-sm transition-colors"
          >
            Get Forms <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <form
          onSubmit={submit}
          data-testid="cheer-registration-form"
          className="bg-white border border-black/10 p-6 sm:p-10 space-y-10"
        >
          {/* Cheerleader */}
          <Section title="Cheerleader Information">
            <Grid>
              <Field label="First Name" required>
                <Input data-testid="cheer-input-first-name" required value={form.athlete_first_name} onChange={update("athlete_first_name")} className="bg-white border-black/20 text-[#0A0A0A] placeholder:text-black/50 rounded-sm" />
              </Field>
              <Field label="Last Name" required>
                <Input data-testid="cheer-input-last-name" required value={form.athlete_last_name} onChange={update("athlete_last_name")} className="bg-white border-black/20 text-[#0A0A0A] placeholder:text-black/50 rounded-sm" />
              </Field>
              <Field label="Age (6 – 13)" required>
                <Input data-testid="cheer-input-age" required type="number" min="6" max="13" value={form.athlete_age} onChange={update("athlete_age")} className="bg-white border-black/20 text-[#0A0A0A] placeholder:text-black/50 rounded-sm" />
              </Field>
              <Field label="Date of Birth">
                <Input data-testid="cheer-input-dob" type="date" value={form.athlete_dob} onChange={update("athlete_dob")} className="bg-white border-black/20 text-[#0A0A0A] placeholder:text-black/50 rounded-sm" />
              </Field>
              <Field label="Uniform Size">
                <Select value={form.shirt_size} onValueChange={(v) => setForm((s) => ({ ...s, shirt_size: v }))}>
                  <SelectTrigger data-testid="cheer-select-uniform-size" className="bg-white border-black/20 text-[#0A0A0A] rounded-sm">
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#F5EFE0] text-[#0A0A0A] border-black/10">
                    {[
                      { v: "YXS", label: "Youth Extra Small" },
                      { v: "YS",  label: "Youth Small" },
                      { v: "YM",  label: "Youth Medium" },
                      { v: "YL",  label: "Youth Large" },
                      { v: "YXL", label: "Youth Extra Large" },
                      { v: "S",   label: "Small" },
                      { v: "M",   label: "Medium" },
                      { v: "L",   label: "Large" },
                      { v: "XL",  label: "XL" },
                    ].map((s) => (
                      <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </Grid>
          </Section>

          {/* Parent */}
          <Section title="Parent / Guardian">
            <Grid>
              <Field label="Full Name" required>
                <Input data-testid="cheer-input-parent-name" required value={form.parent_name} onChange={update("parent_name")} className="bg-white border-black/20 text-[#0A0A0A] placeholder:text-black/50 rounded-sm" />
              </Field>
              <Field label="Phone" required>
                <Input data-testid="cheer-input-parent-phone" required type="tel" value={form.parent_phone} onChange={update("parent_phone")} className="bg-white border-black/20 text-[#0A0A0A] placeholder:text-black/50 rounded-sm" />
              </Field>
              <Field label="Email" required>
                <Input data-testid="cheer-input-parent-email" required type="email" value={form.parent_email} onChange={update("parent_email")} className="bg-white border-black/20 text-[#0A0A0A] placeholder:text-black/50 rounded-sm" />
              </Field>
              <Field label="Home Address" className="md:col-span-2">
                <Input data-testid="cheer-input-address" value={form.address} onChange={update("address")} className="bg-white border-black/20 text-[#0A0A0A] placeholder:text-black/50 rounded-sm" />
              </Field>
            </Grid>
          </Section>

          {/* Emergency */}
          <Section title="Emergency Contact">
            <Grid>
              <Field label="Name">
                <Input data-testid="cheer-input-emergency-name" value={form.emergency_contact_name} onChange={update("emergency_contact_name")} className="bg-white border-black/20 text-[#0A0A0A] placeholder:text-black/50 rounded-sm" />
              </Field>
              <Field label="Phone">
                <Input data-testid="cheer-input-emergency-phone" type="tel" value={form.emergency_contact_phone} onChange={update("emergency_contact_phone")} className="bg-white border-black/20 text-[#0A0A0A] placeholder:text-black/50 rounded-sm" />
              </Field>
            </Grid>
          </Section>

          {/* Medical */}
          <Section title="Medical Notes">
            <Field label="Allergies, conditions, medications">
              <Textarea
                data-testid="cheer-input-medical"
                rows={4}
                value={form.medical_notes}
                onChange={update("medical_notes")}
                placeholder="Anything our coaches and trainers should know"
                className="bg-white border-black/20 text-[#0A0A0A] placeholder:text-black/50 rounded-sm"
              />
            </Field>
          </Section>

          {/* Agreement */}
          <div className="bg-white border border-black/15 p-5 flex items-start gap-3">
            <Checkbox
              id="cheer-agreement"
              data-testid="cheer-checkbox-agreement"
              checked={form.agreement}
              onCheckedChange={(v) => setForm((s) => ({ ...s, agreement: !!v }))}
              className="mt-1 border-black/40 data-[state=checked]:bg-[#2D5F3F] data-[state=checked]:border-[#2D5F3F]"
            />
            <Label htmlFor="cheer-agreement" className="text-black/85 text-sm leading-relaxed cursor-pointer">
              I confirm that the information above is accurate and I agree to provide the required documents
              (Sports Physical, Medical Insurance, Birth Certificate) at first practice. I understand spots are
              limited and registration is non-refundable.
            </Label>
          </div>

          {/* Required docs reminder */}
          <div className="text-xs uppercase tracking-[0.2em] text-black/50 font-display">
            Required at first practice: Sports Physical · Medical Insurance · Birth Certificate
          </div>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
            <p className="text-black/65 text-sm">All-in fee: <span className="text-[#2D5F3F] font-bold">$225 / cheerleader</span> · covers uniform, bow &amp; practice wear · collected at first practice</p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="cheer-submit-registration"
              className="inline-flex items-center justify-center gap-2 bg-[#2D5F3F] hover:bg-[#1B4332] disabled:opacity-60 text-[#FDFBF7] font-display uppercase tracking-wider font-extrabold px-8 py-4 shadow-ink-offset hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting</> : <>Submit Registration <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="font-display text-xs uppercase tracking-[0.3em] text-[#2D5F3F] font-bold mb-6 border-l-2 border-[#2D5F3F] pl-3">{title}</h3>
      {children}
    </div>
  );
}
function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>;
}
function Field({ label, required, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-[#2D5F3F] text-xs font-bold uppercase tracking-[0.2em] mb-2 block">
        {label}{required && <span className="text-[#2D5F3F]"> *</span>}
      </Label>
      {children}
    </div>
  );
}
